import { useState, useEffect, useCallback } from 'react'
import { useParams, Navigate } from 'react-router-dom'
import { supabase } from '../../api/supabase'

// Z73.18 appended spec: generic admin CRUD for the small Descartes standing-data lookup tables
// (transport_modes / declaration_types / declaration_categories) -- same code+description
// pattern as src/pages/Data/PaymentTerms.tsx, parametrized by table since these three are all
// shaped the same way (unlike goods_locations, which has real address fields and gets its own
// dedicated page).

interface TableConfig {
  title: string
  description: string
  extraField?: { key: string; label: string; options: string[] }
}

const TABLES: Record<string, TableConfig> = {
  transport_modes: {
    title: 'Transport Modes',
    description: 'UN/EDIFACT mode-of-transport codes (AA.17/21 TRPT_MODE_CODE) used on eCustoms declarations.',
  },
  declaration_types: {
    title: 'Declaration Types',
    description: 'Box 1 declaration type codes (AA.41 DECLN_TYPE) -- default is IMD.',
    extraField: { key: 'group', label: 'Group', options: ['Normal', 'Supplementary'] },
  },
  declaration_categories: {
    title: 'Declaration Categories',
    description: 'CDS declaration category codes (AA.97 Declaration Category) -- default is H1.',
    extraField: { key: 'direction', label: 'Direction', options: ['import', 'export'] },
  },
}

interface Row {
  code: string
  description: string
  is_active: boolean
  [key: string]: unknown
}

const inputCls = 'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white'

function emptyRow(cfg: TableConfig): Row {
  return { code: '', description: '', is_active: true, ...(cfg.extraField ? { [cfg.extraField.key]: cfg.extraField.options[0] } : {}) }
}

export default function CustomsLookups() {
  const { table } = useParams<{ table: string }>()
  const cfg = table ? TABLES[table] : undefined

  const [rows, setRows] = useState<Row[]>([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState<Row | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!table) return
    setLoading(true)
    const { data } = await supabase.from(table).select('*').order('code')
    setRows((data as Row[]) || [])
    setLoading(false)
  }, [table])

  useEffect(() => { load() }, [load])

  if (!table || !cfg) return <Navigate to="/admin" replace />

  const openAdd = () => { setForm(emptyRow(cfg)); setError(null); setModal(true) }
  const openEdit = (r: Row) => { setForm({ ...r }); setError(null); setModal(true) }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form) return
    if (!form.code.trim() || !form.description.trim()) { setError('Code and Description are required.'); return }
    setSaving(true); setError(null)
    try {
      const { error: err } = await supabase.from(table).upsert(form)
      if (err) throw err
      setModal(false)
      load()
    } catch (err: any) {
      setError(err?.message || 'Unknown error')
    } finally {
      setSaving(false)
    }
  }

  const toggleActive = async (r: Row) => {
    await supabase.from(table).update({ is_active: !r.is_active }).eq('code', r.code)
    load()
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{cfg.title}</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">{cfg.description}</p>
        </div>

        <div className="flex justify-end mb-4">
          <button onClick={openAdd} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
            + Add Code
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
          {loading ? (
            <div className="py-12 text-center text-gray-400">Loading…</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  {['Code', 'Description', ...(cfg.extraField ? [cfg.extraField.label] : []), 'Status', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300 text-xs uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map(r => (
                  <tr key={r.code} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 font-mono font-bold text-gray-900 dark:text-white">{r.code}</td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-200">{r.description}</td>
                    {cfg.extraField && <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{String(r[cfg.extraField.key] ?? '')}</td>}
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${r.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {r.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 space-x-3">
                      <button onClick={() => openEdit(r)} className="text-blue-600 hover:underline text-xs font-medium">Edit</button>
                      <button onClick={() => toggleActive(r)} className={`text-xs font-medium ${r.is_active ? 'text-red-500 hover:underline' : 'text-green-600 hover:underline'}`}>
                        {r.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No codes found</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        <div className="mt-2 text-xs text-gray-400">{rows.length} code{rows.length !== 1 ? 's' : ''}</div>
      </div>

      {modal && form && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">{rows.some(r => r.code === form.code) ? 'Edit Code' : 'Add Code'}</h2>
              <button onClick={() => setModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">✕</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">{error}</div>}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Code *</label>
                <input className={inputCls} value={form.code} onChange={e => setForm(f => f && ({ ...f, code: e.target.value.toUpperCase() }))} maxLength={10} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description *</label>
                <input className={inputCls} value={form.description} onChange={e => setForm(f => f && ({ ...f, description: e.target.value }))} />
              </div>
              {cfg.extraField && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{cfg.extraField.label}</label>
                  <select className={inputCls} value={String(form[cfg.extraField.key] ?? '')} onChange={e => setForm(f => f && ({ ...f, [cfg.extraField!.key]: e.target.value }))}>
                    {cfg.extraField.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              )}
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => f && ({ ...f, is_active: e.target.checked }))} className="h-4 w-4 text-blue-600 rounded" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
              </label>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
                <button type="submit" disabled={saving} className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
